import ChatFloatingWidget from '@/components/chat/ChatFloatingWidget';
import MyContainer from '@/components/MyContainer';
import { Button } from '@/components/ui/button';
import { routes } from '@/routes/route-constants';
import { useCustomer } from '@/services/customer-service';
import { Edit } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import OrderAsyncTable from '../order/component/order-async-table';
import { CustomerDetailError } from './components/customer-detail-error';
import { CustomerDetailLoading } from './components/customer-detail-loading';
import { CustomerInfoCard } from './components/customer-info-card';
import { RecentOrderCard } from './components/recent-order-card';
import { useCustomerChat } from './hooks/use-customer-chat';

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [chatOpen, setChatOpen] = useState(false);

  const { data: customer, isLoading, error } = useCustomer(id);
  const recentOrder = customer?.recentOrder;

  const {
    canChat,
    currentConversation,
    messages,
    isLoadingMessage,
    prevCursor,
    onNewMessage,
    onLoadMoreMessage,
  } = useCustomerChat(customer);

  const handleBack = useCallback(() => setLocation('/customers'), [setLocation]);
  const handleEdit = useCallback(() => setLocation(routes.customer.edit(id)), [setLocation, id]);
  const handleViewOrder = useCallback(
    (orderId: string) => setLocation(`/orders/${orderId}`),
    [setLocation],
  );

  const handleRetry = useCallback(() => window.location.reload(), []);

  if (isLoading) {
    return <CustomerDetailLoading />;
  }

  if (error || !customer) {
    return (
      <CustomerDetailError
        error={error || "We couldn't load the customer details."}
        onRetry={handleRetry}
        onBack={handleBack}
      />
    );
  }

  return (
    <>
      <MyContainer
        title={customer.name}
        subtitle="Customer details and order history"
        actions={
          <Button variant="outline" size="sm" className="gap-2" onClick={handleEdit}>
            <Edit className="w-4 h-4" /> Edit
          </Button>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
             <OrderAsyncTable customerId={id!}/>
          </div>

          <div className="space-y-6">
            <CustomerInfoCard customer={customer} />
            <RecentOrderCard order={recentOrder} onViewOrder={handleViewOrder} />
          </div>
        </div>
      </MyContainer>

      {/* Chat Floating Widget */}
      {canChat && currentConversation && (
        <ChatFloatingWidget
          currentConversation={currentConversation}
          messages={messages}
          isLoadingMessage={isLoadingMessage}
          prevCursor={prevCursor ?? ''}
          OnNewMessage={onNewMessage}
          OnLoadMoreMessage={onLoadMoreMessage}
          open={chatOpen}
          onOpenChange={setChatOpen}
          title={customer.name}
        />
      )}
    </>
  );
}
